// imports for app window layout
import { useAuth0 } from '@auth0/auth0-react';
import MainWindow from '../../app_layout/main_window/MainWindow';
import ChatWindow from '../../app_layout/chat_window/ChatWindow';
import SideBar from '../../app_layout/sidebar/SideBar';
import APP_WINDOW_CONFIG from '../../app_layout/layout_config';

// imports relevant for HTTP calls
import DisplayException from '../../error_messages/DisplayException';
import CircleLoader from 'react-spinners/CircleLoader';
import {SERVER_URL, WEBSOCKET_URL} from '../../config_server_url';

// import user session information
import UserSessionContext from '../../user_session/context_user_session';
import { SetSessionInfo_SetCampaignObjects, SetSessionInfo_ResetCampaignObjects } from '../../user_session/session_info_utils';
import { SetSessionInfo_OnDeleteCurrentProject } from '../../user_session/session_info_utils';

// imports this module specific
import '../../css_styles/button_styles.css'
import classes from './PageCampaignBuilder.module.css';
import WorkflowEditor from '../../experimental_setup/WorkflowEditor'
import WorkflowViewer from '../../experimental_setup/WorkflowViewer'
import DesignSpaceEditor from '../../design_space/DesignSpaceEditor'
import DesignSpaceViewer from '../../design_space/DesignSpaceViewer'
import ObjectiveEditor from '../../objective/ObjectiveEditor'
import ObjectiveViewer from '../../objective/ObjectiveViewer'
import CampaignValidationTable from './CampaignValidationTable'
import YesNoPopup from '../../popup_windows/YesNoPopup';
import InfoPopup from '../../popup_windows/InfoPopup';

import { useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';

function PageCampaignBuilder() {
    // ------------------ Display HTTP/Server Exceptions ---------------------------------
    const [Exception, setException] = useState(false);
    const [ExceptionMessage, setExceptionMessage] = useState('');
    // This Component requires to fetches data on first load -> setIsloading(true);
    // This Component does not fetch data on first load -> setIsloading(false);
    const [isLoading, setIsLoading] = useState(false);

    // ------------------ Load User Session Information ---------------------------------
    const usr_session_ctx = useContext(UserSessionContext);
    const { user } = useAuth0();
    const [broadcast, setBroadcast] = useState([]); //get session notifications via web sockets

    // ------------------ Add User Session Websockets For Notification Events Across All Tenant (Workspace) Members -----------------------------

    useEffect(() => {
        // Create WebSocket connection`${SERVER_URL}/all_projects/load/`
        const ws = new WebSocket(`${WEBSOCKET_URL}/user_session/ws_project/${usr_session_ctx.uuid_tenant}/${user.sub}`);
        // Connection opened
        ws.onopen = () => {
        //   console.log(`WebSocket connection established for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
        };
        ws.onerror = (error) => {
            // console.error("WebSocket error observed:", error);
          };
        // Listen for messages
        ws.onmessage = (event) => {
        //   console.log('Message received:', event.data);
          let parser = event.data.split('__');
          let sender = parser[0]
          let type = parser[1].split('.')[1]
          let project_uuid = parser[2]
          if (type === 'ProjectDelete'){
            if(project_uuid === usr_session_ctx.session_info.uuid_current_project) {
                let new_broadcast = (<>{sender} has deleted <strong>your current project uuid:{project_uuid}</strong></>);
                usr_session_ctx.session_info = SetSessionInfo_OnDeleteCurrentProject(usr_session_ctx);
                handleUpdateUserSession()
                setBroadcast(prevBroadcast => [...prevBroadcast, new_broadcast]);
            }
          }
        };
        // Connection closed
        ws.onclose = () => {
        //   console.log(`WebSocket connection closed for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
        };
        // Clean up WebSocket connection when component unmounts
        return () => {
          ws.close();
        };
      }, []); // Effect runs when tenant_id or user_id changes



      function handleUpdateUserSession() {
        setIsLoading(true);
        let session_update = {key: 'session_info', value: usr_session_ctx.session_info}
        fetch(`${SERVER_URL}/user_session/update_field/`, {method: 'PUT',
                                                           headers:{'Content-Type':'application/json',
                                                                   'auth0_user': user.sub,
                                                                   'uuid_tenant': usr_session_ctx.uuid_tenant }, 
                                                           body:JSON.stringify(session_update)
        })
        .then((res) => {
        if(!res.ok) {
            throw new Error(`HTTP error ${res.status}`);
        }
            return res.json();
        })
        .then((data) => {
            setIsLoading(false);
        })
            .catch((err) => {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        });
   }

    // ------------------ General Window Layout -----------------------------
    
    const [canvasSize, setCanvasSize] = useState({width: 0, height: 0});
    const [layout, setLayout] = useState({sidebar:0, main: 0, chat: 0});
    const [toggleSideBar, setToggleSideBar] = useState(false);


    useEffect(() => {
        function handleResize() {
            let window_height = window.innerHeight;
            let window_width = window.innerWidth;
            let canvas_height = window_height - APP_WINDOW_CONFIG.OFFSET_HEADER_YPOS_PX;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_PX;
            let chat = (window_width - sidebar)*APP_WINDOW_CONFIG.CHAT_WIDTH_RELATIVE;
            chat = Math.max (chat, APP_WINDOW_CONFIG.MIN_CHAT_WIDTH_PX);
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setCanvasSize({width: window_width, height: canvas_height});
            // if currently the sidebar is not toggled, then resize the sidbar widow to toggle_size
            if(toggleSideBar){
                sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_COLLAPSED_PX;
                main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            }
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        // Set initial height
        handleResize();
        // Add event listener to handle window resize
        window.addEventListener('resize', handleResize);
        // Remove event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [toggleSideBar]); // Empty dependency array ensures the effect only runs once


    function handleToggle() {
        // if currently the sidebar is not toggled, then resize the sidbar widow to toggle_size
        if(!toggleSideBar){
            let window_width = window.innerWidth;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_COLLAPSED_PX;
            let chat = layout.chat;
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        else
        {
            let window_width = window.innerWidth;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_PX;
            let chat = layout.chat;
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        setToggleSideBar(!toggleSideBar);
    }

    // ------------------ Side Bar Content -----------------------------

    const sidebar = (<>
        <SideBar width={layout.sidebar} 
                toggle={toggleSideBar} 
                handle_toggle={handleToggle} 
                active_link='campaign_builder' >
        </SideBar>
    </>)

    // ------------------ Main Window Content ----------------------------
    
    const [isEditor, setIsEditor] = useState(false);
    const [campaignTitle, setCampaignTitle] = useState('');
    const [viewerMode, setViewerMode] = useState('ExperimentalWorkflow'); 
    const [validationWindow, setValidationWindow] = useState(false);
    const [validationMessage, setValidationMessage] = useState([]);
    const [showCreateSuccess, setShowCreateSuccess] = useState(false);
    const [uuidNewCampaign, setUuidNewCampaign] = useState('');
    const [isExpWorkflow, setIsExpWorkflow] = useState(false);
    const [alertCurrentUnderUpdate, setAlertCurrentUnderUpdate] = useState(false);

    const campaign_objects = {uuid_experimental_setup:'', uuid_design_space:'', uuid_objective:''};
    const navigate = useNavigate();
   
    // initialie campaign_objects from context user_session:
    campaign_objects.uuid_experimental_setup = usr_session_ctx.session_info.on_create_campaign.uuid_experimental_setup;
    campaign_objects.uuid_design_space = usr_session_ctx.session_info.on_create_campaign.uuid_design_space;
    campaign_objects.uuid_objective = usr_session_ctx.session_info.on_create_campaign.uuid_objective;

    useEffect( () => {
        if (usr_session_ctx.session_info.on_create_campaign.uuid_experimental_setup !== '') { setIsExpWorkflow(true); }
        else { setIsExpWorkflow(false); }
    }, [usr_session_ctx.session_info.on_create_campaign.uuid_experimental_setup] );
   


    // ------------ functional handlers ------------------------

    function handleCampaignTitle(event){
        setCampaignTitle(event.target.value)
    }

    function handleOnExitEditor(){
        setIsEditor(false)
    }

    function handleOnEdit(){
        setIsEditor(true);
    }


    async function handleSessionUpdateOnSaveWorkflow( majorChangeDesignSpace, 
                                                      majorChangeObjective,
                                                      objects){

        if(campaign_objects.uuid_experimental_setup === ''){
            let valid_exp_setup = true;
            let valid_design_space = false;
            let valid_objective = false;
            const updatedSessionInfo = SetSessionInfo_SetCampaignObjects(usr_session_ctx, 
                                                                              objects.uuid_exp_setup,
                                                                              objects.uuid_design_space, 
                                                                              objects.uuid_objective,
                                                                              valid_exp_setup,
                                                                              valid_design_space,
                                                                              valid_objective);
            usr_session_ctx.session_info = updatedSessionInfo;                                                                    
            let session_update = { key: 'session_info', value: updatedSessionInfo };
            const response2 = await fetch(`${SERVER_URL}/user_session/update_field/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            },
            body: JSON.stringify(session_update)
            });
            if (!response2.ok) {
                throw new Error(`HTTP error ${response2.status}`);
            }
        }
        else{ 
            // update SessionInfo - ExperimentalSetup is validated; validation status of DesignSpace and Objectives remain unchanged, unless majurChanges have been detected that 
            // may result in invalid DesignSpace/Objectives -> in this case the status of DesignSpace/Objectives is set to unvalidated
            let valid_exp_setup = true;
            let valid_design_space = usr_session_ctx.session_info.on_create_campaign.validated_design_space;
            let valid_objective = usr_session_ctx.session_info.on_create_campaign.validated_objective;
            if (majorChangeDesignSpace) { valid_design_space = false;}
            if (majorChangeObjective) { valid_objective = false;}

            const updatedSessionInfo = SetSessionInfo_SetCampaignObjects(usr_session_ctx, 
                                                                              objects.uuid_exp_setup,
                                                                              objects.uuid_design_space, 
                                                                              objects.uuid_objective,
                                                                              valid_exp_setup,
                                                                              valid_design_space,
                                                                              valid_objective);
            usr_session_ctx.session_info = updatedSessionInfo;                                                                    
            let session_update = { key: 'session_info', value: updatedSessionInfo };
            const response2 = await fetch(`${SERVER_URL}/user_session/update_field/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            },
            body: JSON.stringify(session_update)
            });
            if (!response2.ok) {
                throw new Error(`HTTP error ${response2.status}`);
            }
        }
    }


    async function handleSessionUpdateOnSaveDesignSpace(objects){
        let valid_exp_setup = usr_session_ctx.session_info.on_create_campaign.validated_experimental_setup;
        let valid_design_space = true;
        let valid_objective = usr_session_ctx.session_info.on_create_campaign.validated_objective;
        const updatedSessionInfo = SetSessionInfo_SetCampaignObjects(usr_session_ctx, 
                                                                          objects.uuid_experimental_setup,
                                                                          objects.uuid_design_space, 
                                                                          objects.uuid_objective,
                                                                          valid_exp_setup,
                                                                          valid_design_space,
                                                                          valid_objective);
        usr_session_ctx.session_info = updatedSessionInfo;                                                                    
        let session_update = { key: 'session_info', value: updatedSessionInfo };
        const response2 = await fetch(`${SERVER_URL}/user_session/update_field/`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'auth0_user': user.sub,
            'uuid_tenant': usr_session_ctx.uuid_tenant
        },
        body: JSON.stringify(session_update)
        });
        if (!response2.ok) {
            throw new Error(`HTTP error ${response2.status}`);
        }
    }

    async function handleSessionUpdateOnSaveObjective(objects){
        let valid_exp_setup = usr_session_ctx.session_info.on_create_campaign.validated_experimental_setup;
        let valid_design_space = usr_session_ctx.session_info.on_create_campaign.validated_design_space;
        let valid_objective = true;
        const updatedSessionInfo = SetSessionInfo_SetCampaignObjects(usr_session_ctx, 
                                                                          objects.uuid_experimental_setup,
                                                                          objects.uuid_design_space, 
                                                                          objects.uuid_objective,
                                                                          valid_exp_setup,
                                                                          valid_design_space,
                                                                          valid_objective);
        usr_session_ctx.session_info = updatedSessionInfo;                                                                    
        let session_update = { key: 'session_info', value: updatedSessionInfo };
        const response2 = await fetch(`${SERVER_URL}/user_session/update_field/`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'auth0_user': user.sub,
            'uuid_tenant': usr_session_ctx.uuid_tenant
        },
        body: JSON.stringify(session_update)
        });
        if (!response2.ok) {
            throw new Error(`HTTP error ${response2.status}`);
        }
    }


    async function handleSessionUpdateOnCreateCampaign(mark_current){
        const updatedSessionInfo = SetSessionInfo_ResetCampaignObjects(usr_session_ctx, mark_current, uuidNewCampaign);
        usr_session_ctx.session_info = updatedSessionInfo;  
        setIsLoading(true);  
        try{                                                          
            let session_update = { key: 'session_info', value: updatedSessionInfo };
            const response2 = await fetch(`${SERVER_URL}/user_session/update_field/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            },
            body: JSON.stringify(session_update)
            });
            if (!response2.ok) {
                throw new Error(`HTTP error ${response2.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
        setIsLoading(false);
    }


    async function handleCreateCampaign(){
        // Create Campaign, then update session
        setIsLoading(true);
        try{
            let campaign_data = { title: campaignTitle, 
                                  campaign_objects: campaign_objects,
                                  uuid_project: usr_session_ctx.session_info.uuid_current_project};
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_project/create_opt_campaign/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(campaign_data)
            });
            if(response.ok){
                const data = await response.json();
                setShowCreateSuccess(true);
                setUuidNewCampaign(data.data)
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }

    async function handleMarkCurrentYes(){
        // update UserSession -> update field 'uuid_current_project';
        if (usr_session_ctx.session_info.on_update_campaign.uuid_experimental_setup !== '' ||
            usr_session_ctx.session_info.on_update_campaign.uuid_design_space !== '' ||
            usr_session_ctx.session_info.on_update_campaign.uuid_objective !== ''){
           setAlertCurrentUnderUpdate(true)
           setShowCreateSuccess(false);
           let mark_current = false
           await handleSessionUpdateOnCreateCampaign(mark_current);
        }
        else {
            const mark_current = true;
            await handleSessionUpdateOnCreateCampaign(mark_current);
            setShowCreateSuccess(false);
            setViewerMode('ExperimentalWorkflow');
            setIsExpWorkflow(false);
            navigate('/active_campaign_setup');
        }
    }

    async function handleMarkCurrentNo(){
        const mark_current = false;
        await handleSessionUpdateOnCreateCampaign(mark_current);
        setShowCreateSuccess(false);
        setViewerMode('ExperimentalWorkflow')
        setIsExpWorkflow(false);
        navigate('/optimization_campaigns');
    }



    // ----------- Define Navigation Bar to switch between viewer modes: Experimental Setup, Design Space, Objective

    function selectViewerMode(event){
        event.preventDefault();
        setViewerMode(event.target.id);
        setIsEditor(false);
    }

    let classEW = 'black';
    let classDS = 'black';
    let classO = 'black';

    let viewer = (<></>);
    let editor = (<></>);
    if (viewerMode === 'ExperimentalWorkflow'){
        classEW = 'selectionLink selected';
        classDS = 'selectionLink';
        classO = 'selectionLink';
        viewer=(<>
            <WorkflowViewer uuid_experimental_setup={campaign_objects.uuid_experimental_setup} 
                                width={layout.main} 
                                height={canvasSize.height-155} 
                                onEdit={handleOnEdit}/>
            </>);
        editor=(<>
            <WorkflowEditor campaign_objects={campaign_objects}
                            width={layout.main}
                            height={canvasSize.height-100}
                            exit={handleOnExitEditor}
                            handleSessionUpdate={handleSessionUpdateOnSaveWorkflow}/>
            </>);
    }
    if (viewerMode === 'DesignSpace'){
        classEW = 'selectionLink';
        classDS = 'selectionLink selected';
        classO = 'selectionLink';
        viewer=(<>
            <DesignSpaceViewer campaign_objects={campaign_objects}
                               width={layout.main}
                               height={canvasSize.height-155}
                               onEdit={handleOnEdit}
                               validated={usr_session_ctx.session_info.on_create_campaign.validated_design_space}/>
        </>);
        editor=(<>
            <DesignSpaceEditor campaign_objects={campaign_objects}
                               width={layout.main}
                               height={canvasSize.height-100}
                               exit={handleOnExitEditor}
                               handleSessionUpdate={handleSessionUpdateOnSaveDesignSpace}
                               validated={usr_session_ctx.session_info.on_create_campaign.validated_design_space}
                               handleSessionUpdateOnSaveWorkflow={handleSessionUpdateOnSaveWorkflow}/>
        </>);
    }

    if (viewerMode === 'Objective'){
        classEW = 'selectionLink';
        classDS = 'selectionLink';
        classO = 'selectionLink selected';
        viewer=(<>
            <ObjectiveViewer   campaign_objects={campaign_objects} 
                               width={layout.main}
                               height={canvasSize.height-155}
                               onEdit={handleOnEdit}
                               validated={usr_session_ctx.session_info.on_create_campaign.validated_objective}/>
        </>);
        editor=(<>
            <ObjectiveEditor campaign_objects={campaign_objects}
                             width={layout.main}
                             height={canvasSize.height-100}
                             exit={handleOnExitEditor}
                             handleSessionUpdate={handleSessionUpdateOnSaveObjective}
                             validated={usr_session_ctx.session_info.on_create_campaign.validated_objective}
                             handleSessionUpdateOnSaveWorkflow={handleSessionUpdateOnSaveWorkflow} />
        </>);
    }


    function onValidateDesignSpace(){
        setViewerMode('DesignSpace');
        setIsEditor(true);
        setValidationWindow(false);
        setValidationMessage([]);
    }

    function onValidateObjective(){
        setViewerMode('Objective');
        setIsEditor(true);
        setValidationWindow(false);
        setValidationMessage([]);
    }


    // -------------------- Implement functions Create/Discard ----------------------------
    function onCreateCampaign(){
        // check if DesignSpace has been validated
        let valid = true;
        let validation_msg = []
        if (!usr_session_ctx.session_info.on_create_campaign.validated_design_space) { 
            valid=false;
            validation_msg.push({msg:'Design Space requires (re)validation', action:onValidateDesignSpace});
        }
        if (!usr_session_ctx.session_info.on_create_campaign.validated_objective) { 
            valid=false;
            validation_msg.push({msg:'Objective requires (re)validation', action:onValidateObjective});
        }
        if (!valid) {
            setValidationWindow(!valid);
            setValidationMessage(validation_msg);
        }
        else {
            handleCreateCampaign();
        }
    }

    async function handleDiscardCampaign(){
        const status = await deleteCampaignObjects(campaign_objects);
        if(status === 'ok'){
            const res = await resetSessionInfo();
            if(res === 'ok'){
                setIsExpWorkflow(false);
            }
        }
    }

    async function deleteCampaignObjects(campaign_objects){
        setIsLoading(true);  
        try{                                                          
            const response = await fetch(`${SERVER_URL}/current_project/delete_campaign_objects/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            },
            body: JSON.stringify(campaign_objects)
            });
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
            return 'ok';
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
        setIsLoading(false);
    }


    async function resetSessionInfo(mark_current=false, uuidNewCampaign=''){
        const updatedSessionInfo = SetSessionInfo_ResetCampaignObjects(usr_session_ctx, mark_current, uuidNewCampaign);
        usr_session_ctx.session_info = updatedSessionInfo;  
        setIsLoading(true);  
        try{                                                          
            let session_update = { key: 'session_info', value: updatedSessionInfo };
            const response = await fetch(`${SERVER_URL}/user_session/update_field/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            },
            body: JSON.stringify(session_update)
            });
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
            return 'ok';
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
        setIsLoading(false);
    }

    function handleExitValidation(){
        setValidationWindow(false);
        setValidationMessage([]);
    }


    // -------------------- Create Main Window -----------------------------------------------
    let mainWindow = (<MainWindow width={layout.main} />);
    if(!isLoading && !Exception && broadcast.length === 0){
        if(alertCurrentUnderUpdate) {
        mainWindow =(<>
            <MainWindow width={layout.main} >
                    <InfoPopup ypos={'60px'} handleOk={()=>{setAlertCurrentUnderUpdate(false); navigate('/optimization_campaigns')}}>  
                    The campaign cannot be marked as current because the active campaign is currently being updated.
                    <strong> Please complete the update first. </strong><br/><br/>
                    The optimization campaign (uuid:{uuidNewCampaign}) has been created and is now available.
                    </InfoPopup>
            </MainWindow>
                </>);
        }
        // if no workflow is selected or created
        else if (!isEditor && !validationWindow && !isExpWorkflow && !showCreateSuccess) { 
            mainWindow = (<>
                <MainWindow width={layout.main} >
                    <div style={{fontSize:'18px', position:'relative', textAlign:'center', height:'25px', marginTop:'50px', width:'100%'}} >
                        {/* To get started create or load an experimental workflow. */}
                        To get started create an experimental workflow.
                    </div> 
                    <div style={{position: 'relative', display: 'flex', gap:'20px', justifyContent:'center', marginTop:'25px', left:'3%', width:'94%'}}>
                        <button className={'actionButton'} onClick={handleOnEdit} >Create</button>
                        {/* <button className={'actionButton'} style={{marginRight:'0px'}}>Load</button> */}
                    </div>                
                </MainWindow>
            </>);
        }

        else if (!isEditor && !validationWindow && isExpWorkflow && !showCreateSuccess) { 
            mainWindow = (<>
                <MainWindow width={layout.main} >
                    <input id='title' style = {{'marginTop': '20px', 'marginBottom': '0px', 'border':'none', 'outline':'none', 'padding':'5px', position: 'relative',
                                                'borderLeft':'0.5px solid var(--aixelo-gray-light)', 'fontSize': '24px', 'fontWeight':'bold', left:'3%', width:'94%'}} 
                                    onChange = {handleCampaignTitle} value={campaignTitle} autoComplete="off" type="text" placeholder="Enter Campaign Title"  name="project_title" />
                    <div style={{position:'relative', left:'5%', top:'12px', display:'flex', gap:'10px', marginTop:'5px', zIndex:'100', width:'fit-content'}}>        
                        <button id='ExperimentalWorkflow' className={classEW} onClick={selectViewerMode} >Experimental Workflow</button>
                        <button id='DesignSpace' className={classDS} onClick={selectViewerMode}>Design Space</button>
                        <button id='Objective'className={classO} onClick={selectViewerMode}>Objective</button>
                    </div>    
                    <div className = {classes.inner_canvas} style={{height:canvasSize.height-160, paddingTop:'20px'}}>
                        {viewer}
                    </div>
                <div style={{postion:'relative', display:'flex', marginLeft:'3%', marginTop:'18px',  marginBottom:'18px', height:'fit-content', width:'fit-content'}}>
                    <button className={'actionButton'} style={{width:'160px', left:'3%'}} onClick={onCreateCampaign}>Create Campaign</button> 
                    <button className={'actionButton'} style={{width:'160px', left:'3%'}} onClick={handleDiscardCampaign}>Discard Campaign</button>   
                </div> 
                </MainWindow>
            </>);
        }


        else if (!validationWindow && isEditor && !showCreateSuccess) { 
            // Regular Content and Logic of Main Window Goes Here
            mainWindow = (<>
                <MainWindow width={layout.main} >
                    {editor}
                </MainWindow>
            </>);
        }

        else if(showCreateSuccess){
            editor = (<>
                <div className = {'builder_canvas'} >
                    <YesNoPopup ypos={'60px'} handleYes={handleMarkCurrentYes} handleNo={handleMarkCurrentNo} >
                            The optimization campaign (uuid:{uuidNewCampaign}) has been created! <br/>
                            Mark the created campaign as current?
                    </YesNoPopup>  
                </div>
            </>);
            mainWindow = (<>
                <MainWindow width={layout.main} >
                    {editor}
                </MainWindow>
                </>);
        }


        else if(validationWindow) {
        
            let action_panel = (
                <>
                    <div style={{marginTop:'25px'}}>
                        <div style={{marginLeft:'20px', marginBottom:'5px', fontSize:'14px'}}>Feel free to click 'Exit' to fix inconsistencies directly in the respective editor(s).</div>
                        <div style={{display:'flex', margin:'0px'}}>
                            <button id='validate_button' className={'actionButtonSmall'} style={{marginLeft: '20px', right: '0px', marginBottom:'10px'}} onClick={handleExitValidation}>Exit</button>
                        </div>
                    </div>
                </>
                );

            let show_validations = (<>
                                        <div style={{position: 'relative', marginLeft:'20px', marginTop:'25px', height:'fit-content', width:`${layout.main * 0.95 - 60}px`, overflow:'auto'}}>
                                            <CampaignValidationTable validationMessage={validationMessage}/>
                                        </div>
                                    </>);
            editor = (<>
                        <div className = {'builder_canvas'} style={{height:`${canvasSize.height-155}px`}} >
                            <h3 style={{marginTop:'10px', marginLeft:'20px', marginBottom:'10px', padding:'0px', color:'var(--aixelo-red)'}}>Campaign Validation</h3>
                            <div style={{marginLeft:'20px', marginBottom:'0px', fontSize:'14px'}}>Following issues have been detected.</div>
                            {show_validations}
                            {action_panel}
                        </div>
                    </>);

        mainWindow = (<>
            <MainWindow width={layout.main} >
                {editor}
            </MainWindow>
            </>);
        }
    }

    if (!isLoading && !Exception && broadcast.length > 0){
        mainWindow = (<>
            <MainWindow width={layout.main} >
            <div style={{position: 'relative', left:'3%', width:'94%', height:'100%'}}>
                <InfoPopup ypos={'60px'} handleOk={()=>{setBroadcast(prevBroadcast => []); navigate('/')} } >
                    <div style={{fontWeight:'bold', textAlign:'left'}}>Session has been updated by another user:</div>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', textAlign: 'left' }}>
                        {broadcast.map((item, index) => (
                        <li key={index}>{item}</li>
                        ))}
                    </ul>
                </InfoPopup>
                </div>
            </MainWindow>
            </>);
    }



    if (isLoading && !Exception) {   
        mainWindow = (<>
                <MainWindow width={layout.main} >
                    <div style = {{position:'absolute', top:'5px', display: 'flex', justifyContent: 'center', alignItems: 'center',  width:'100%', height:'100%', border:'1px solid black'}}>
                        <CircleLoader loading={isLoading} size={40} color='var(--aixelo-green)' />
                    </div>
                </MainWindow>
            </>);
       }


    // Exection and Error Messeges Get Priority and Are Evaluated Before Anything els
    if(Exception){
        mainWindow = (<>
                <MainWindow width={layout.main} >
                    <DisplayException>
                        {ExceptionMessage}
                    </DisplayException>  
                </MainWindow>
            </>);
       }


// ------------------ Return All Components ----------------------------

    // Show Sidebar, MainWindow and ChatWindow
    return(
        <>
        <div style={{position:'relative', display:'grid', gridTemplateColumns: 'auto auto auto', overflow: 'hidden', left:`${APP_WINDOW_CONFIG.OFFSET_WINDOW_LEFT_PX}px`, top:`${APP_WINDOW_CONFIG.OFFSET_HEADER_YPOS_PX}px`, height:canvasSize.height, width:'100%'}}>
            {sidebar}
            {mainWindow}
            <ChatWindow width={layout.chat}/>
        </div>    
        </>
    );

}


export default PageCampaignBuilder;